/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { Component, EventEmitter, Input, Output } from '@angular/core';

import { FilterOption, FilterType, REMOVE_FILTER } from '../../../models/filter-option';

/**
 * Reusable filter control for the Infrastructure list pages.
 *
 * Supports two modes:
 *  - Single mode (default): renders the FILTER dropdown and, when a real filter
 *    is selected, one type-aware value input. Two-way bound via [(selected)].
 *  - Multiple mode ([multiple]="true"): the dropdown ADDS filters to an active
 *    list ([(active)]); each active filter shows its own value input and a
 *    remove button, and the filters are AND'd together by the backend.
 *
 * Emits filterChanged whenever a filter or its value changes so the host page
 * can re-run its query.
 */
@Component({
  selector: 'app-filter-bar',
  templateUrl: './filter-bar.component.html',
})
export class FilterBarComponent {
  /** Expose the enum to the template. */
  FilterType = FilterType;

  /** All filter options available (include REMOVE_FILTER to allow clearing). */
  @Input() options: FilterOption[] = [];

  /** When true, the bar keeps a list of active filters instead of one. */
  @Input() multiple = false;

  /** The currently selected filter option (single mode). */
  @Input() selected: FilterOption = REMOVE_FILTER;

  /** Supports two-way binding: [(selected)]. */
  @Output() selectedChange = new EventEmitter<FilterOption>();

  /** The active filters (multiple mode). */
  @Input() active: FilterOption[] = [];

  /** Supports two-way binding: [(active)]. */
  @Output() activeChange = new EventEmitter<FilterOption[]>();

  /** Emitted whenever the active filter(s) or a value changes. */
  @Output() filterChanged = new EventEmitter<FilterOption>();

  /** User picked a different filter option from the dropdown (single mode). */
  onOptionClicked(option: FilterOption): void {
    if (this.selected === option) {
      return;
    }

    // Reset the value to a sensible default for the option's type so a value
    // entered for one filter doesn't leak into the next.
    option.value = option.type === FilterType.BOOLEAN ? false : '';

    this.selected = option;
    this.selectedChange.emit(option);
    this.filterChanged.emit(option);
  }

  /** User picked an option from the dropdown (multiple mode). */
  onOptionAdded(option: FilterOption): void {
    // The "REMOVE FILTER" / no-column option clears all active filters.
    if (option == null || option.column == null) {
      this.active = [];
      this.emitActiveChanged();
      return;
    }

    // Don't add the same column twice.
    if (this.active.some(filter => filter.column === option.column)) {
      return;
    }

    // Add a fresh copy so the shared dropdown option is never mutated.
    this.active = [
      ...this.active,
      new FilterOption({
        column: option.column,
        label: option.label,
        type: option.type,
        value: option.type === FilterType.BOOLEAN ? false : '',
      }),
    ];
    this.emitActiveChanged();
  }

  /** User removed one active filter (multiple mode). */
  removeFilter(filter: FilterOption): void {
    this.active = this.active.filter(f => f !== filter);
    this.emitActiveChanged();
  }

  /** User changed the value of the active filter (single mode). */
  onValueChanged(): void {
    this.filterChanged.emit(this.selected);
  }

  private emitActiveChanged(): void {
    this.activeChange.emit(this.active);
    this.filterChanged.emit(this.selected);
  }
}
